import { useState, useEffect, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Camera, X, Scan, AlertCircle } from 'lucide-react';

const BarcodeScanner = ({ onScan, onClose }) => {
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState('');
  const [permissionDenied, setPermissionDenied] = useState(false);
  const html5QrCodeRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    
    // Auto-start scanning on mount
    startScanning();
    
    return () => {
      mountedRef.current = false;
      stopScanning();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startScanning = async () => {
    setError('');
    setPermissionDenied(false);

    try {
      // Check for camera permission first
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      stream.getTracks().forEach(track => track.stop());
      
      if (!mountedRef.current) return;
      
      setIsScanning(true);
      
      // Small delay to ensure DOM is ready
      await new Promise(resolve => setTimeout(resolve, 100));
      
      if (!mountedRef.current) return;
      
      html5QrCodeRef.current = new Html5Qrcode("barcode-reader");
      
      await html5QrCodeRef.current.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: { width: 280, height: 150 },
          aspectRatio: 1.5,
          disableFlip: false
        },
        (decodedText) => {
          // Success - stop scanning and callback
          if (mountedRef.current) {
            stopScanning();
            onScan(decodedText);
          }
        },
        () => {
          // Ignore continuous errors during scanning
        }
      );
    } catch (err) {
      // camera error handled by UI
      if (mountedRef.current) {
        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          setPermissionDenied(true);
          setError("Permission caméra refusée. Veuillez autoriser l'accès dans les paramètres de votre navigateur.");
        } else if (err.name === 'NotFoundError') {
          setError("Aucune caméra détectée sur cet appareil.");
        } else {
          setError("Impossible d'accéder à la caméra. Vérifiez les permissions.");
        }
        setIsScanning(false);
      }
    }
  };

  const stopScanning = async () => {
    if (html5QrCodeRef.current) {
      try {
        const state = html5QrCodeRef.current.getState();
        if (state === 2) { // SCANNING
          await html5QrCodeRef.current.stop();
        }
      } catch (err) {
        // Ignore stop errors
      }
      html5QrCodeRef.current = null;
    }
    if (mountedRef.current) {
      setIsScanning(false);
    }
  };

  const handleManualInput = (e) => {
    if (e.key === 'Enter' && e.target.value.trim()) {
      onScan(e.target.value.trim());
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/95 z-50 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white text-lg flex items-center gap-2">
            <Scan className="w-5 h-5 text-[color:var(--color-primary)]" />
            Scanner le code barre
          </h3>
          <button
            type="button"
            onClick={() => {
              stopScanning();
              onClose();
            }}
            className="p-2 hover:bg-white/10 transition-colors rounded"
            data-testid="close-scanner"
          >
            <X className="w-6 h-6 text-white" />
          </button>
        </div>

        {/* Permission Denied Help */}
        {permissionDenied && (
          <div className="bg-red-500/10 border border-red-500/30 p-4 mb-4 rounded">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-red-400 font-medium mb-2">Permission caméra requise</p>
                <p className="text-zinc-400 text-sm mb-3">
                  Pour scanner les codes barres, vous devez autoriser l'accès à la caméra.
                </p>
                <div className="text-zinc-500 text-xs space-y-1">
                  <p><strong>Chrome/Edge:</strong> Cliquez sur l'icône caméra dans la barre d'adresse</p>
                  <p><strong>Safari:</strong> Paramètres &gt; Safari &gt; Caméra</p>
                  <p><strong>Firefox:</strong> Cliquez sur l'icône de permission à gauche de l'URL</p>
                </div>
                <button
                  type="button"
                  onClick={startScanning}
                  className="mt-4 text-[color:var(--color-primary)] text-sm hover:underline"
                >
                  Réessayer
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Scanner Area */}
        <div 
          id="barcode-reader" 
          className="w-full bg-black border border-white/20 overflow-hidden"
          style={{ minHeight: isScanning ? '300px' : '200px' }}
        />

        {/* Error Message */}
        {error && !permissionDenied && (
          <p className="text-red-400 text-sm mt-4 text-center">{error}</p>
        )}

        {/* Controls */}
        <div className="mt-4 flex justify-center">
          {!isScanning ? (
            <button
              type="button"
              onClick={startScanning}
              className="flex items-center gap-2 bg-[color:var(--color-primary)] text-black px-6 py-3 hover:bg-[color:var(--color-primary-hover)] transition-colors"
              data-testid="start-scanner"
            >
              <Camera className="w-5 h-5" />
              Activer la caméra
            </button>
          ) : (
            <div className="text-center">
              <div className="flex items-center gap-2 text-[color:var(--color-primary)] mb-2">
                <div className="w-2 h-2 bg-[color:var(--color-primary)] rounded-full animate-pulse" />
                Scanner actif
              </div>
              <button
                type="button"
                onClick={stopScanning}
                className="text-zinc-500 hover:text-white text-sm transition-colors"
                data-testid="stop-scanner"
              >
                Arrêter
              </button>
            </div>
          )}
        </div>

        {/* Manual Input */}
        <div className="mt-6 pt-6 border-t border-white/10">
          <p className="text-zinc-500 text-center text-sm mb-3">Ou entrez le code manuellement:</p>
          <input
            type="text"
            placeholder="Code barre (EAN/UPC)..."
            onKeyDown={handleManualInput}
            className="w-full bg-black/50 border border-white/20 text-white px-4 py-3 text-center outline-none focus:border-[color:var(--color-primary)] transition-colors"
            data-testid="manual-barcode-input"
            autoComplete="off"
          />
        </div>
      </div>
    </div>
  );
};

export default BarcodeScanner;
